import {useCallback, useEffect, useMemo, useState} from 'react';

import {getAccountPreset, updateAccountPreset} from 'src/openapi/admin/apis/AccountPresetApi';
import {EntitlementPreset} from 'src/openapi/admin/models/EntitlementPreset';
import {useAppDispatch} from 'src/store/hooks';

export type AccountPreset = {
    presetId: string,
    presetName: string,
    presetDescription: string,
};

const emptyPreset = {presetId: '', presetName: '', presetDescription: ''};

export function useAccountPreset(accountId: number) {
    const apiDispatch = useAppDispatch();
    const [preset, setPreset] = useState<AccountPreset>(emptyPreset);
    const [initialPreset, setInitialPreset] = useState<AccountPreset>(emptyPreset);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!accountId) {
            return;
        }

        setLoading(true);
        setError(null);

        apiDispatch(getAccountPreset({accountId}))
            .then(response => {
                const payload = response.payload;
                const data = payload.data as EntitlementPreset;
                if (payload.success && data) {
                    const loadedPreset = {
                        presetId: data.id || '',
                        presetName: data.name || '',
                        presetDescription: data.description || '',
                    };
                    setPreset(loadedPreset);
                    setInitialPreset(loadedPreset);
                } else {
                    setPreset(emptyPreset);
                    setInitialPreset(emptyPreset);
                    setError('Failed to load Account preset');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err);
                setLoading(false);
            });
    }, [accountId, apiDispatch]);

    const savePreset = useCallback(() => {
        if (!accountId) {
            return;
        }

        return apiDispatch(updateAccountPreset({accountId, presetId: preset.presetId}))
            .then(() => {
                setInitialPreset(preset);
            })
            .catch(setError);
    }, [accountId, apiDispatch, preset]);

    const discardChanges = () => {
        setPreset(initialPreset);
        setError(null);
    };

    const isChanged = useMemo(() => {
        return (
            preset!.presetId !== initialPreset!.presetId ||
            preset!.presetName !== initialPreset!.presetName ||
            preset!.presetDescription !== initialPreset!.presetDescription
        );
    }, [preset, initialPreset]);

    return {preset, setPreset, savePreset, loading, error, isChanged, discardChanges};
}
